import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEntityFilter } from '../context/EntityFilterContext';
import api from '../utils/api';
import EmailMonitorView from './EmailMonitor.view';

const PAGE_SIZE = 50;

export default function EmailMonitor() {
  const { user } = useAuth();
  const { selectedEntity } = useEntityFilter();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const [filters, setFilters] = useState({
    entity: '',
    category: '',
    status: '',
    date_from: '',
    date_to: '',
    search: '',
  });
  const [sortBy, setSortBy] = useState('received_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [offset, setOffset] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const listParams = { limit: PAGE_SIZE, offset, sort_by: sortBy, sort_order: sortOrder };
      Object.entries(filters).forEach(([k, v]) => { if (v) listParams[k] = v; });

      const statsParams = {};
      if (filters.date_from) statsParams.date_from = filters.date_from;
      if (filters.date_to) statsParams.date_to = filters.date_to;

      // Header entity selector overrides the on-page entity filter when set.
      // Server intersects against the user's allowed entities.
      if (selectedEntity && selectedEntity !== 'all') {
        listParams.entity  = selectedEntity;
        statsParams.entity = selectedEntity;
      }

      const [listRes, statsRes] = await Promise.all([
        api.get('/email-log', { params: listParams }),
        api.get('/email-log/stats', { params: statsParams }),
      ]);
      setRows(listRes.data.rows || []);
      setTotal(listRes.data.total || 0);
      setStats(statsRes.data);
    } catch (err) {
      console.error('email-log load error:', err);
      setRows([]); setTotal(0); setStats(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    // Load entities for super_admin filter
    if (user?.role === 'super_admin') {
      api.get('/entities').then(r => setEntities(r.data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => { load(); }, [filters, offset, sortBy, sortOrder, selectedEntity]); // eslint-disable-line

  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setOffset(0);
  };
  const clearFilters = () => {
    setFilters({ entity: '', category: '', status: '', date_from: '', date_to: '', search: '' });
    setOffset(0);
  };
  const toggleSort = (col) => {
    if (sortBy === col) setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    else { setSortBy(col); setSortOrder('desc'); }
    setOffset(0);
  };

  return (
    <EmailMonitorView
      rows={rows}
      total={total}
      stats={stats}
      entities={entities}
      loading={loading}
      filters={filters}
      onFilterChange={setFilter}
      onClearFilters={clearFilters}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={toggleSort}
      offset={offset}
      pageSize={PAGE_SIZE}
      onPage={(dir) => dir === 'prev' ? setOffset(Math.max(0, offset - PAGE_SIZE)) : setOffset(offset + PAGE_SIZE)}
      onRowClick={(row) => setSelected(row)}
      onRefresh={load}
      onCloseDetail={() => setSelected(null)}
      onOpenTicket={(id) => navigate('/tickets/' + id)}
      selected={selected}
      currentUser={user}
    />
  );
}
